"use client";

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./dialog";
import { Typography } from "./typography";
import { X } from "lucide-react";
import Image from "next/image";
import {
  useEffect,
  useState,
  useRef,
  useCallback,
  ReactNode,
  SyntheticEvent,
} from "react";
import {
  CreatorVideoControls,
  CreatorVideoPlayer,
} from "./creator-video-player/index";

type Props = {
  isOpen: boolean;
  title: string;
  videoSrc: string;
  onCloseAction: () => void;
  logoUrl?: string;
  logoAlt?: string;
  logoComponent?: ReactNode;
  renderActions?: () => ReactNode;
  autoplay?: boolean;
};

/**
 * Fullscreen video modal with a fixed-height header and bottom controls.
 */
export const CreatorVideoPlayerModal = ({
  isOpen,
  title,
  videoSrc,
  onCloseAction,
  logoUrl,
  logoAlt = "Logo",
  logoComponent,
  renderActions,
  autoplay = false,
}: Props) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const videoContainerRef = useRef<HTMLDivElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [videoWidth, setVideoWidth] = useState(0);
  const shouldAutoplayRef = useRef(false);

  // Helper to safely update duration from various sources
  const updateDuration = useCallback((dur: number) => {
    if (dur && Number.isFinite(dur) && dur > 0) {
      setDuration(dur);
      return true;
    }
    return false;
  }, []);

  // Video event handlers
  const handleLoadedMetadata = (event: SyntheticEvent<HTMLVideoElement>) => {
    updateDuration(event.currentTarget.duration);
    // Explicitly seek to first frame to ensure it renders (prevents black screen)
    event.currentTarget.currentTime = 0;
  };

  const handleTimeUpdate = (event: SyntheticEvent<HTMLVideoElement>) => {
    setCurrentTime(event.currentTarget.currentTime);
  };

  const handleLoadedData = () => {
    const video = videoRef.current;
    const rect = video?.getBoundingClientRect();
    setVideoWidth(rect?.width ?? 0);

    if (video?.duration) {
      updateDuration(video.duration);
    }
  };

  const handleDurationChange = (event: SyntheticEvent<HTMLVideoElement>) => {
    updateDuration(event.currentTarget.duration);
  };

  const handleCanPlay = (event: SyntheticEvent<HTMLVideoElement>) => {
    updateDuration(event.currentTarget.duration);

    // Attempt autoplay if flag is set
    if (shouldAutoplayRef.current) {
      shouldAutoplayRef.current = false;
      const video = event.currentTarget;
      video
        .play()
        .then(() => setIsPlaying(true))
        .catch(() => setIsPlaying(false)); // Autoplay blocked by browser
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
  };

  const handlePause = () => {
    setIsPlaying(false);
  };

  const handlePlay = () => {
    setIsPlaying(true);
  };

  // Reset video state when modal is closed, set autoplay flag when opened
  useEffect(() => {
    if (!isOpen) {
      const video = videoRef.current;
      if (video) {
        video.pause();
        video.currentTime = 0;
      }

      setIsPlaying(false);
      setCurrentTime(0);
      setDuration(0);
      shouldAutoplayRef.current = false;
    } else if (autoplay) {
      // Set flag to autoplay when video is ready
      shouldAutoplayRef.current = true;
    }
  }, [isOpen, autoplay]);

  // Tracks video duration and width updates (important for blob URLs and responsive timeline)
  useEffect(() => {
    const video = videoRef.current;
    const container = videoContainerRef.current;
    if (!video || !container || !isOpen) {
      return;
    }

    // Duration tracking
    const checkDuration = () => updateDuration(video.duration);

    // Width tracking
    const updateWidth = () => {
      const rect = video.getBoundingClientRect();
      setVideoWidth(rect.width);
    };

    const onResize = () => {
      requestAnimationFrame(updateWidth);
    };

    // Combined event handler for metadata/data loaded events
    const onVideoLoaded = () => {
      checkDuration();
      updateWidth();
    };

    // Setup: Duration events
    video.addEventListener("loadedmetadata", onVideoLoaded);
    video.addEventListener("loadeddata", onVideoLoaded);
    video.addEventListener("durationchange", checkDuration);
    video.addEventListener("canplay", checkDuration);

    // Setup: Width tracking
    onResize(); // Initial width update
    const observer = new ResizeObserver(onResize);
    observer.observe(container);
    window.addEventListener("resize", onResize);

    // Poll for duration until found with exponential backoff (some blob URLs don't fire events properly)
    const maxPollingDuration = 5000;
    const pollingStartTime = Date.now();
    let pollTimeout: ReturnType<typeof setTimeout> | undefined;

    const pollDuration = (delay: number) => {
      pollTimeout = setTimeout(() => {
        const hasDuration = updateDuration(video.duration);
        const elapsed = Date.now() - pollingStartTime;

        if (hasDuration || elapsed >= maxPollingDuration) {
          return;
        }

        const nextDelay = Math.min(delay * 2, 1600);
        pollDuration(nextDelay);
      }, delay);
    };

    pollDuration(100);

    return () => {
      // Cleanup: Duration events
      video.removeEventListener("loadedmetadata", onVideoLoaded);
      video.removeEventListener("loadeddata", onVideoLoaded);
      video.removeEventListener("durationchange", checkDuration);
      video.removeEventListener("canplay", checkDuration);
      if (pollTimeout) {
        clearTimeout(pollTimeout);
      }

      // Cleanup: Width tracking
      observer.disconnect();
      window.removeEventListener("resize", onResize);
    };
  }, [videoSrc, isOpen, updateDuration]);

  const onTogglePlayAction = async () => {
    const video = videoRef.current;

    if (!video) {
      return;
    }

    if (video.paused) {
      try {
        await video.play();
        setIsPlaying(true);
      } catch {
        // Playback can fail due to browser autoplay policies or missing user interaction.
        setIsPlaying(false);
      }
      return;
    }

    video.pause();
    setIsPlaying(false);
  };

  const onOpenChangeAction = (open: boolean) => {
    if (!open) {
      onCloseAction();
    }
  };

  const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;

  const onSeek = useCallback(
    (percent: number) => {
      const video = videoRef.current;
      if (!video || !duration) {
        return;
      }
      const newTime = (percent / 100) * duration;
      video.currentTime = newTime;
      setCurrentTime(newTime);
    },
    [duration],
  );

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChangeAction}>
      <DialogContent
        className="flex h-full w-screen max-w-none flex-col overflow-hidden rounded-none! border-none bg-black p-0 lg:top-0 lg:left-0 lg:h-screen lg:max-h-none lg:max-w-none lg:translate-x-0 lg:translate-y-0"
        onInteractOutside={(event) => event.preventDefault()}
        onEscapeKeyDown={() => onCloseAction()}
        hideCloseButton
      >
        <DialogTitle className="hidden">{title}</DialogTitle>

        <DialogHeader className="flex h-7 shrink-0 flex-row items-center justify-between bg-black px-2 text-white shadow-none">
          {/* Logo & Title */}
          <div className="flex items-center gap-2">
            {logoComponent ? (
              logoComponent
            ) : logoUrl ? (
              <Image
                src={logoUrl}
                alt={logoAlt}
                width={34}
                height={34}
                className="rounded-full object-cover"
                quality={5}
                preload
              />
            ) : null}
            <Typography
              size="sm"
              className="leading-2.75 text-white"
              weight="semibold"
            >
              {title}
            </Typography>
          </div>

          {/* Close */}
          <DialogClose
            className="inline-flex cursor-pointer items-center justify-center rounded-xs transition-opacity"
            aria-label="Close video"
          >
            <X className="h-3 w-3 text-white" />
          </DialogClose>
        </DialogHeader>

        {/* Video Content */}
        <CreatorVideoPlayer
          videoSrc={videoSrc}
          videoRef={videoRef}
          videoContainerRef={videoContainerRef}
          videoWidth={videoWidth}
          progressPercent={progressPercent}
          onLoadedMetadata={handleLoadedMetadata}
          onTimeUpdate={handleTimeUpdate}
          onLoadedData={handleLoadedData}
          onDurationChange={handleDurationChange}
          onCanPlay={handleCanPlay}
          onEnded={handleEnded}
          onPause={handlePause}
          onPlay={handlePlay}
          onSeek={onSeek}
        />

        <DialogFooter className="relative flex h-13.5 w-full flex-row items-center justify-center! bg-black px-4 py-0">
          <CreatorVideoControls
            isPlaying={isPlaying}
            onTogglePlayAction={onTogglePlayAction}
          />
          {renderActions && renderActions()}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
